/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */

import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { handleCommaSeparatedAmountWithoutDec } from '../../../../SharedModules/CommonUtilities/commonUtilities';
import { dispatchTableLoader } from '../../../../SharedModules/Table/store/Actions';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const getHeader = (currentPage, pageCount, data) => {
  const { qtr, prinDate, requestedBy, reportName, requestorUserId } = data;
  const header = {
    alignment: 'center',
    fontSize: 10,
    margin: [28.8, 20, 28.8, 25.92],
    style: 'tableExample',
    table: {
      widths: [230, 230, 240],
      body: [
        [
          {
            text: [{ text: 'Requested By: ', bold: true }, requestedBy],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: reportName,
            fontSize: 12,
            bold: true,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Date Printed: ', bold: true }, prinDate],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: [{ text: ' ', bold: true }, ''],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: qtr,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
  return header;
};

const contentFont = 9;
const pageMargins = [28.8, 80, 28.8, 25.92];
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 10],
  },
  subheader: {
    fontSize: 16,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  tableExample: {
    margin: [0, 5, 0, 15],
  },
  tableHeader: {
    bold: true,
    fontSize: 13,
    color: 'black',
  },
};

const valueFormatter = (value) => {
  if (value && !value?.toString()?.includes('(') && value >= 0) {
    const formattedValue = value?.split('$')[1]?.trim();
    return handleCommaSeparatedAmountWithoutDec(
      value?.toString()?.includes('$') ? formattedValue : value
    );
  } else if (
    value &&
    value?.toString()?.includes(')') &&
    value?.toString()?.includes('(')
  ) {
    const dollarVal = !value?.toString()?.includes('$')
      ? value
      : value?.split('$')[1]?.trim();
    const innerValue = !dollarVal?.toString()?.includes('(')
      ? dollarVal
      : dollarVal?.split(')')[0]?.trim();
    return `($ ${handleCommaSeparatedAmountWithoutDec(innerValue.substring(1))})`;
  } else if (value < 0) {
    return `($ ${parseFloat(value)
      ?.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .substring(1)})`;
  }
  if (value === '' || !value) {
    return value;
  }
};

const getTableRows = (rows, columns) => {
  const dataToReturn = [];
  const headers = columns.map((item) => item.headerName);
  dataToReturn.push([
    {
      text: 'Drug Rebate \n\nRebate Quarter',
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
    },
    {
      text: headers[1],
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
    },
    {
      text: headers[2],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[3],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[4],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[5],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[6],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
  ]);
  const border = [true, true, true, true];
  const noBorder = [false, false, false, false];

  for (let i = 0; i < rows.length; i += 1) {
    const row1 = [
      {
        text: rows[i].field1,
        fontSize: contentFont,
        color: '',
        border: rows[i].field1 === '' ? noBorder : border,
      },

      {
        text: valueFormatter(rows[i].field2),
        fontSize: contentFont,
        alignment: 'right',
        color: rows[i].field2 < 0 ? 'red' : '',
        border: rows[i].field2 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].field3),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].field3 < 0 ? 'red' : '',
        border: rows[i].field3 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].field4),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].field4 < 0 ? 'red' : '',
        border: rows[i].field4 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].field5),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].field5 < 0 ? 'red' : '',
        border: rows[i].field5 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].field6),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].field6 < 0 ? 'red' : '',
        border: rows[i].field6 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].field7),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].field7 < 0 ? 'red' : '',
        border: rows[i].field7 === '' ? noBorder : border,
      },
    ];
    dataToReturn.push(row1);
    if (rows[i].field1 === '') {
      dataToReturn.push(row1);
      dataToReturn.push(row1);
      dataToReturn.push(row1);
      dataToReturn.push(row1);
      dataToReturn.push(row1);
    }
  }
  return dataToReturn;
};

const getTableBody = (bodyData, columns, agencyName, state, quarter) => {
  return [
    {
      style: 'tableExample',
      color: '#444',
      table: {
        widths: [180, 80, 80, 80, 80, 80, 80],
        body: [
          [
            {
              text: 'MEDICAID DRUG REBATE SCHEDULE',
              style: 'tableHeader',
              colSpan: 3,
              rowSpan: 3,
              alignment: 'left',
              marginTop: 15,
            },
            {},
            {},
            {
              text: { text: `STATE :${state}`, bold: true },
              colSpan: 4,
              alignment: 'left',
              fontSize: contentFont,
              color: 'black',
            },
            {},
            {},
            {},
          ],
          [
            {},
            {},
            {},
            {
              text: {
                text: `QUARTER ENDED : (${quarter.replace(')', '')})`,
                bold: true,
              },
              colSpan: 4,
              alignment: 'left',
              fontSize: contentFont,
              color: 'black',
            },
            {},
            {},
            {},
          ],
          [
            {},
            {},
            {},
            {
              text: { text: `AGENCY : ${agencyName}`, bold: true },
              colSpan: 4,
              alignment: 'left',
              fontSize: contentFont,
              color: 'black',
            },
            {},
            {},
            {},
          ],
          ...getTableRows(bodyData, columns),
        ],
      },
    },
  ];
};

const getPdfData = (bodyData, columns, headerData) => {
  const { agencyName } = headerData;
  const { state } = headerData;
  const { quarter } = headerData;
  const content = getTableBody(bodyData, columns, agencyName, state, quarter);
  const currentYear = new Date().getFullYear();
  return {
    header: (currentPage, pageCount) => {
      return getHeader(currentPage, pageCount, headerData);
    },
    footer: () => {
        const footers = [];
        footers.push({
          text:`${currentYear} Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.`,
          fontSize: 8,
          color: '#636363',
          fontFamily: 'Arial',
          alignment: 'center',
        });

      return footers;
    },
    content,
    pageMargins,
    styles,
    pageSize: {
      width: 780,
      height: 700,
    },
    // pageOrientation: 'landscape',
  };
};

export const exportPDF = (
  data,
  UserDefaultValues,
  columns,
  getRebateProgramName,
  fileName,
  state,
  logInUser,
  rebateProgramCd,
  dispatch
) => {
  const headerData = {
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    requestedBy:
      UserDefaultValues && UserDefaultValues.userId
        ? UserDefaultValues.userId
        : logInUser,
    reportName: 'CMS 64 9r',
    qtr: rebateProgramCd,
    requestorUserId: '',
    agencyName:
      getRebateProgramName &&
      getRebateProgramName.cms64AgencyName &&
      getRebateProgramName.cms64AgencyName.split(' ')[0],
    state: state,
    quarter:
      columns &&
      columns[1] &&
      columns[1].headerName.split(' ')[2].replace('(', ''),
  };

  const pdfData = getPdfData(data, columns, headerData);

  pdfMake.createPdf(pdfData).download(`${fileName}.pdf`);
  dispatch(dispatchTableLoader(false));
};
